import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { RecipeIngredient } from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import { isOwnStayIngredientResponseWithStaticInfo } from '@libs/search-service-smart-search';
import type {
  ActivityStaticInfo,
  ActivitySuggestionWithStaticInfo,
  ChronologicalItinerary,
  ItineraryMainIngredientResponseWithStaticInfo,
} from '@libs/trip-planner-interfaces';

import type { ItinerarySegment } from '../../models';
import { ItinerarySegmentType } from '../../models';
import type { ItineraryOwnStaySegment } from '../../models/itinerarySegments/ownStay';

import {
  getActivitySegments,
  getUppsellSegment,
} from './tripOverviewActivitySegmentsLogic';
import {
  getCityText,
  getTripOverviewDateLocationSegment,
} from './tripOverviewLocationSegmentsLogic';

const { t } = i18n.global;

export function getOwnStayRelatedSegments(
  ingredient: ItineraryMainIngredientResponseWithStaticInfo,
  getIngredient: (id: string) => RecipeIngredient,
  ingredientIndex: number,
  areThereActivitiesInDestination: boolean,
  activitySuggestions: ActivitySuggestionWithStaticInfo[],
  staticActivitySuggestions: ActivityStaticInfo[],
  moneyFormanterFn: (amount: number, currency: string) => string,
  itineraryByPeriod: ChronologicalItinerary,
  selectedDate?: Date
): ItinerarySegment[] {
  const segments: ItinerarySegment[] = [];
  if (isOwnStayIngredientResponseWithStaticInfo(ingredient)) {
    const ownStayIngredient = getIngredient(
      ingredient.ingredientId
    ) as OwnStayIngredient;

    segments.push(
      getTripOverviewDateLocationSegment(itineraryByPeriod, {
        ingredientId: ingredient.ingredientId,
        numberOfNights: ingredient.numberOfNights,
        displayText: ownStayIngredient.displayText,
      })
    );

    if (ingredient.numberOfNights !== 0) {
      const ownStaySegment: ItineraryOwnStaySegment = {
        segmentType: ItinerarySegmentType.OwnStay,
        addAccommodationMessage: `${t(
          'planner.no_availability.actions.explore_hotels_selection_in'
        )} ${getCityText(ownStayIngredient.displayText)}`,
        context: {
          ingredientId: ingredient.ingredientId,
          ingredientIndex,
        },
      };

      segments.push(ownStaySegment);
    }

    segments.push(
      ...getActivitySegments(
        ingredient,
        getIngredient,
        ingredientIndex,
        selectedDate
      )
    );

    const upsellSegment = getUppsellSegment(
      ownStayIngredient,
      areThereActivitiesInDestination,
      activitySuggestions,
      staticActivitySuggestions,
      moneyFormanterFn
    );

    if (upsellSegment) {
      segments.push(upsellSegment);
    }
  }

  return segments;
}
